'use client';

import { Suspense, useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, PartyPopper } from 'lucide-react';

import { WizardProgressBar } from '@/components/signup/progress-bar';
import {
  AccountStep,
  OrganizationStep,
  PlanStep,
  BrandingStep,
  ServicesStep,
  ReviewStep,
} from '@/components/signup/wizard-steps';
import { useSignupWizard } from '@/stores/signupWizardStore';
import { signUp } from '@/lib/auth/client';
import { ensureSessionReady } from '@/lib/auth/ensure-session-ready';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/components/ui/button';
import { fetchPublicBillingPlans } from '@/lib/api';
import { resolveCommercialSelection } from '@glonextrack/contracts';
import {
  applyIndustryServiceBundle,
  industryFromTenantPackage,
  isSameServiceSelection,
  normalizeIndustrySelection,
  resolveTenantPackage,
  TENANT_PACKAGE_LABELS,
} from '@/lib/industry-packages';

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 40 : -40,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -40 : 40,
    opacity: 0,
  }),
};

const packageWalkthroughSequence = {
  core: [
    'Set core organization profile and tracking scope.',
    'Choose subscription, branding, and baseline modules.',
    'Review and launch the tenant workspace.',
  ],
  insurance: [
    'Confirm insurer organization profile and package assignment.',
    'Choose pricing, branding, and insurance-optimized workload sizing.',
    'Review insurer modules and launch tenant + mobile-ready access.',
  ],
  farming: [
    'Confirm farming organization profile and package assignment.',
    'Choose pricing, branding, and farm operations workload sizing.',
    'Review farming telemetry modules and launch tenant + mobile-ready access.',
  ],
} as const;

function SignupPageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const session = useSession();
  const store = useSignupWizard();
  const {
    currentStep,
    updatePlan,
    updateOrganization,
    updateServices,
    setSubmitting,
    setSubmitError,
  } = store;
  const [direction, setDirection] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const normalizedIndustry = normalizeIndustrySelection(store.organization.industry);
  const activeTenantPackage =
    store.organization.tenantPackage || resolveTenantPackage(normalizedIndustry);
  const walkthroughSteps = packageWalkthroughSequence[activeTenantPackage];

  // Pre-select plan from URL
  useEffect(() => {
    const planParam = searchParams.get('plan');
    if (planParam) {
      updatePlan({ tier: planParam });
    }
  }, [searchParams, updatePlan]);

  // Pre-select industry/package from URL
  useEffect(() => {
    const industryParam =
      searchParams.get('industry') ??
      searchParams.get('tenantType') ??
      searchParams.get('industryType');
    const packageParam = searchParams.get('package');
    const industryFromPackage = industryFromTenantPackage(packageParam);

    const preferredIndustry =
      industryFromPackage ||
      normalizeIndustrySelection(industryParam);
    if (!preferredIndustry) return;

    const tenantPackage = resolveTenantPackage(preferredIndustry);
    const bundledServices = applyIndustryServiceBundle(
      store.services.enabledServices,
      preferredIndustry,
    );

    if (
      store.organization.industry !== preferredIndustry ||
      store.organization.tenantPackage !== tenantPackage
    ) {
      updateOrganization({
        industry: preferredIndustry,
        tenantPackage,
      });
    }

    if (!isSameServiceSelection(bundledServices, store.services.enabledServices)) {
      updateServices({ enabledServices: bundledServices });
    }
  }, [searchParams, store.organization.industry, store.organization.tenantPackage, store.services.enabledServices, updateOrganization, updateServices]);

  // Track direction for animations
  useEffect(() => {
    const unsub = useSignupWizard.subscribe(
      (state, prev) => {
        if (state.currentStep !== prev.currentStep) {
          setDirection(state.currentStep > prev.currentStep ? 1 : -1);
        }
      },
    );
    return unsub;
  }, []);

  const handleSubmit = async () => {
    const { account, organization, branding, services, plan } = store;
    setSubmitting(true);
    setSubmitError(null);
    const normalizedIndustry = normalizeIndustrySelection(organization.industry);
    const tenantPackage = organization.tenantPackage || resolveTenantPackage(normalizedIndustry);
    const enabledServices = applyIndustryServiceBundle(
      services.enabledServices ?? [],
      normalizedIndustry,
    );
    const enabledServicesPayload = Object.fromEntries(
      enabledServices.map((key) => [key, true]),
    );
    let resolvedPlan = {
      tier: plan.tier,
      billingCycle: plan.billingCycle,
      seatCount: plan.seatCount,
      vehicleCount: plan.vehicleCount,
      deviceCount: plan.deviceCount,
    };

    try {
      try {
        const planCatalog = await fetchPublicBillingPlans();
        if (planCatalog.length > 0) {
          const resolvedCommercial = resolveCommercialSelection({
            plans: planCatalog,
            requestedPlanId: plan.tier,
            billingCycle: plan.billingCycle,
            seatCount: plan.seatCount,
            vehicleCount: plan.vehicleCount,
            deviceCount: plan.deviceCount,
            industry: normalizedIndustry,
            tenantPackage,
          });
          resolvedPlan = {
            tier: resolvedCommercial.plan.id,
            billingCycle: resolvedCommercial.billingCycle,
            seatCount: resolvedCommercial.counts.seatCount,
            vehicleCount: resolvedCommercial.counts.vehicleCount,
            deviceCount: resolvedCommercial.counts.deviceCount,
          };
          if (
            resolvedPlan.tier !== plan.tier ||
            resolvedPlan.billingCycle !== plan.billingCycle ||
            resolvedPlan.seatCount !== plan.seatCount ||
            resolvedPlan.vehicleCount !== plan.vehicleCount ||
            resolvedPlan.deviceCount !== plan.deviceCount
          ) {
            updatePlan(resolvedPlan);
          }
        }
      } catch {
        // Non-fatal: continue with the current plan if the public catalog is unavailable.
      }

      // 1. Create user & org via signup
      const result = await signUp({
        firstName: account.firstName,
        lastName: account.lastName,
        organizationName: organization.name,
        countryCode: organization.country,
        email: account.email,
        password: account.password,
        planId: resolvedPlan.tier,
        billingCycle: resolvedPlan.billingCycle,
        seatCount: resolvedPlan.seatCount,
        vehicleCount: resolvedPlan.vehicleCount,
        deviceCount: resolvedPlan.deviceCount,
        enabledServices: enabledServicesPayload,
        branding: {
          logoUrl: branding.logoPreview || '',
          primaryColor: branding.primaryColor,
          tagline: branding.tagline,
          timezone: branding.timezone,
        },
        industry: normalizedIndustry,
        tenantPackage,
        region: organization.region,
        website: organization.website,
        fleetSize: organization.fleetSize,
      });

      if (!result.ok) {
        setSubmitError(result.error ?? 'Signup failed. Please try again.');
        setSubmitting(false);
        return;
      }

      if (result.data?.provisioningWarning) {
        const provisioningMessages =
          result.data.provisioningErrorDetails
            ?.map((entry) => entry.message)
            .filter(Boolean)
            .join(' ');
        setSubmitError(
          provisioningMessages
            ? `Your account was created, but workspace provisioning is incomplete: ${provisioningMessages} Please do not submit the form again; retry sign-in later or contact support.`
            : 'Your account was created, but workspace provisioning is incomplete. Please do not submit the form again; retry sign-in later or contact support.',
        );
        setSubmitting(false);
        return;
      }

      // 2. Refresh session to get tenant info
      const hydrated = await ensureSessionReady(session, {
        attempts: 5,
        initialDelayMs: 350,
      });
      if (!hydrated) {
        setSubmitError(
          'Account was created, but session sync is still in progress. Please retry in a moment.',
        );
        setSubmitting(false);
        return;
      }

      // 3. Update tenant settings with branding, services, plan info
      try {
        const meRes = await fetch('/api/auth/me');
        const meData = await meRes.json();
        const tenantId = meData?.tenant?.id;

        if (tenantId) {
          await fetch(`/api/v1/organizations/${tenantId}/tenant-settings`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              branding: {
                logoUrl: branding.logoPreview || '',
                primaryColor: branding.primaryColor,
                tagline: branding.tagline,
                timezone: branding.timezone,
              },
              enabledServices: enabledServicesPayload,
              plan: resolvedPlan.tier,
              billingCycle: resolvedPlan.billingCycle,
              fleetSize: organization.fleetSize,
              industry: normalizedIndustry,
              tenantPackage,
            }),
          });
        }
      } catch {
        // Non-fatal: tenant settings update can be retried later
      }

      // 4. Show success
      setShowSuccess(true);
      setTimeout(() => {
        router.push('/app');
      }, 3000);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success Screen
  if (showSuccess) {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center px-6 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 10 }}
          className="mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-emerald-100"
        >
          <PartyPopper className="h-12 w-12 text-emerald-600" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl font-bold text-slate-900"
        >
          Your fleet platform is live!
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-3 text-base text-slate-500"
        >
          Redirecting you to your dashboard in a moment...
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6"
        >
          <Button variant="accent" className="rounded-full" onClick={() => router.push('/app')}>
            Go to Dashboard <Sparkles className="h-4 w-4" />
          </Button>
        </motion.div>
      </div>
    );
  }

  const steps = [
    <AccountStep key="account" />,
    <OrganizationStep key="organization" />,
    <PlanStep key="plan" />,
    <BrandingStep key="branding" />,
    <ServicesStep key="services" />,
    <ReviewStep key="review" onSubmit={handleSubmit} />,
  ];

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 lg:px-8 lg:py-16">
      {/* Progress Bar */}
      <div className="mb-12">
        <WizardProgressBar />
      </div>

      {activeTenantPackage !== 'core' && (
        <div className="mb-8 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Industry Walkthrough Sequence
          </p>
          <p className="mt-1 text-sm font-semibold text-emerald-900">
            {TENANT_PACKAGE_LABELS[activeTenantPackage]}
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {walkthroughSteps.map((step, index) => (
              <div key={step} className="rounded-lg border border-emerald-100 bg-white/80 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">
                  Sequence {index + 1}
                </p>
                <p className="mt-1 text-xs text-slate-600">{step}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step Content */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentStep}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {steps[currentStep]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={null}>
      <SignupPageContent />
    </Suspense>
  );
}
